/**
 * The Field of Play — a Decision & Coordination Field.
 *
 * A stable "court" for business work, in the spirit of a coach's clipboard.
 * The geography is fixed so that *position carries meaning*: the horizontal
 * axis is the progression of coordinated action (Signal → Learn) and the
 * vertical axis is the locus of concern (Organization → Individual). Typed
 * pieces ("tokens") are placed on the field; their shape is their class, their
 * position is their stage and level, and overlays carry state.
 *
 * `readField` is the machine-readable interpreter: it turns a placed field into
 * structured observations, tensions (anti-patterns), and suggested plays — the
 * "show the state, suggest the move" pattern. This module is the single source
 * of truth for the surface; the page at /field-of-play renders from it.
 */

/* ------------------------------------------------------------------ */
/* The surface: stages (horizontal) × levels (vertical)               */
/* ------------------------------------------------------------------ */

export type StageId = 'signal' | 'frame' | 'decide' | 'commit' | 'execute' | 'learn';
export type LevelId = 'org' | 'team' | 'individual';
export type Accent = 'sky' | 'indigo' | 'amber' | 'violet' | 'emerald' | 'teal';

export interface Stage {
  id: StageId;
  name: string;
  question: string;
  accent: Accent;
}

/** Left → right: the movement from raw reality to coordinated action. */
export const STAGES: Stage[] = [
  { id: 'signal', name: 'Signal', question: 'What is changing or coming in?', accent: 'sky' },
  { id: 'frame', name: 'Frame', question: 'What does it mean? How do we see it?', accent: 'indigo' },
  { id: 'decide', name: 'Decide', question: 'What choice must be made?', accent: 'amber' },
  { id: 'commit', name: 'Commit', question: 'Who owns it? What is agreed?', accent: 'violet' },
  { id: 'execute', name: 'Execute', question: 'What is the active work?', accent: 'emerald' },
  { id: 'learn', name: 'Learn', question: 'What happened? What do we adapt?', accent: 'teal' },
];

export interface Level {
  id: LevelId;
  name: string;
  description: string;
}

/** Top → bottom: the locus of concern. */
export const LEVELS: Level[] = [
  { id: 'org', name: 'Organization', description: 'Strategic, cross-team, enterprise-wide' },
  { id: 'team', name: 'Team', description: 'Group coordination and shared work' },
  { id: 'individual', name: 'Individual', description: 'Personal scope and execution' },
];

export const STAGE_ORDER: StageId[] = STAGES.map((s) => s.id);

/** Numeric position of a stage in the flow (0 = Signal … 5 = Learn). */
export function stageIndex(stage: StageId): number {
  return STAGE_ORDER.indexOf(stage);
}

/* ------------------------------------------------------------------ */
/* The pieces: token grammar                                          */
/* ------------------------------------------------------------------ */

export type TokenType =
  | 'actor'
  | 'decision'
  | 'work'
  | 'risk'
  | 'constraint'
  | 'commitment'
  | 'unknown'
  | 'evidence';

export interface TokenDef {
  type: TokenType;
  name: string;
  /** Visual shape the piece reads as. */
  shape: string;
  /** What this piece represents. */
  meaning: string;
  /** Lucide icon name used to render it. */
  icon: string;
  accent: 'sky' | 'amber' | 'indigo' | 'red' | 'slate' | 'emerald' | 'gray' | 'teal';
}

export const TOKEN_DEFS: TokenDef[] = [
  { type: 'actor', name: 'Actor', shape: 'Circle', meaning: 'A person, team, or system', icon: 'Users', accent: 'sky' },
  { type: 'decision', name: 'Decision', shape: 'Diamond', meaning: 'A choice that must be made', icon: 'Diamond', accent: 'amber' },
  { type: 'work', name: 'Work Item', shape: 'Square', meaning: 'A unit of active work', icon: 'Square', accent: 'indigo' },
  { type: 'risk', name: 'Risk', shape: 'Triangle', meaning: 'A hazard or threat', icon: 'AlertTriangle', accent: 'red' },
  { type: 'constraint', name: 'Constraint', shape: 'Boundary', meaning: 'A limit, scope, or permission', icon: 'Lock', accent: 'slate' },
  { type: 'commitment', name: 'Commitment', shape: 'Linked marker', meaning: 'An agreement with ownership', icon: 'Handshake', accent: 'emerald' },
  { type: 'unknown', name: 'Unknown', shape: 'Dashed shape', meaning: 'Unresolved ambiguity', icon: 'HelpCircle', accent: 'gray' },
  { type: 'evidence', name: 'Evidence', shape: 'Data chip', meaning: 'An observation or data point', icon: 'FileText', accent: 'teal' },
];

export function tokenDef(type: TokenType): TokenDef {
  return TOKEN_DEFS.find((d) => d.type === type)!;
}

/* ------------------------------------------------------------------ */
/* Overlays: state applied to a piece                                 */
/* ------------------------------------------------------------------ */

export type Priority = 'low' | 'med' | 'high';
export type Confidence = 'low' | 'med' | 'high';
export type Reversibility = 'reversible' | 'irreversible';

export interface PlacedToken {
  id: string;
  type: TokenType;
  label: string;
  stage: StageId;
  level: LevelId;
  priority?: Priority;
  confidence?: Confidence;
  reversibility?: Reversibility;
  owner?: string;
}

/* ------------------------------------------------------------------ */
/* The plays: standard moves                                          */
/* ------------------------------------------------------------------ */

export interface Play {
  id: string;
  name: string;
  trigger: string;
  move: string;
  /** Optional cross-link path inside the app. */
  link?: { label: string; to: string };
  secondaryLink?: { label: string; to: string };
}

export const PLAYS: Play[] = [
  {
    id: 'clarify-before-commit',
    name: 'Clarify Before Commit',
    trigger: 'Unresolved ambiguity or low confidence sits close to a commitment.',
    move: 'Convert unknowns into explicit options or assumptions, and resolve them before agreement or resourcing.',
  },
  {
    id: 'escalate',
    name: 'Escalate the Decision',
    trigger: 'A decision is high-stakes, low-confidence, or irreversible at the current level.',
    move: 'Move the decision up a level and require explicit approval before action.',
  },
  {
    id: 'delegate',
    name: 'Delegate (Transfer Agency)',
    trigger: 'Work or a decision needs an owner, or authority should move to a delegate.',
    move: 'Assign ownership with an explicit level of bounded agency, boundaries, and escalation triggers.',
    link: { label: 'Open the Bounded Agency model', to: '/elevation/bounded-agency-delegation' },
    secondaryLink: { label: 'Build delegation in Lab', to: '/elevation/lab/delegation-authority' },
  },
  {
    id: 'reframe',
    name: 'Reframe the Problem',
    trigger: 'The field is execution-heavy with little framing or discovery in play.',
    move: 'Pull work back to Signal/Frame, restate the problem, and re-derive the options.',
  },
  {
    id: 'surface-constraint',
    name: 'Surface the Constraint',
    trigger: 'A live risk or hidden limit is shaping the work without being named.',
    move: 'Make the constraint or risk explicit on the field so it can be planned around or removed.',
  },
  {
    id: 'sequence',
    name: 'Sequence the Work',
    trigger: 'Many items are in flight at once with unclear order or dependencies.',
    move: 'Order the work, set what must come first, and limit work-in-progress.',
  },
  {
    id: 'close-loop',
    name: 'Close the Feedback Loop',
    trigger: 'Execution has produced outcomes but nothing has moved to Learn.',
    move: 'Capture evidence of what happened and feed adaptations back into Frame.',
  },
];

export function play(id: string): Play | undefined {
  return PLAYS.find((p) => p.id === id);
}

/* ------------------------------------------------------------------ */
/* The interpreter: read the field                                    */
/* ------------------------------------------------------------------ */

export type Severity = 'info' | 'warn' | 'risk';

export interface Tension {
  id: string;
  severity: Severity;
  message: string;
  /** Id of a play that addresses this tension. */
  playId?: string;
  /** Tokens implicated in this tension. */
  tokenIds: string[];
}

export interface FieldReading {
  observations: string[];
  tensions: Tension[];
}

const STAGE_NAME: Record<StageId, string> = Object.fromEntries(
  STAGES.map((s) => [s.id, s.name])
) as Record<StageId, string>;

/**
 * Interpret a placed field into structured observations and tensions. Pure and
 * deterministic so AI, the UI, and tests all read the same state the same way.
 */
export function readField(tokens: PlacedToken[]): FieldReading {
  const observations: string[] = [];
  const tensions: Tension[] = [];

  if (tokens.length === 0) {
    return { observations: ['The field is empty. Place pieces to describe the situation.'], tensions: [] };
  }

  // --- Observations (neutral description of state) ---
  const byStage = new Map<StageId, PlacedToken[]>();
  for (const t of tokens) {
    const list = byStage.get(t.stage) ?? [];
    list.push(t);
    byStage.set(t.stage, list);
  }
  const busiest = [...byStage.entries()].sort((a, b) => b[1].length - a[1].length)[0];
  observations.push(`${tokens.length} piece${tokens.length === 1 ? '' : 's'} on the field.`);
  if (busiest) {
    observations.push(`The busiest zone is ${STAGE_NAME[busiest[0]]} (${busiest[1].length}).`);
  }
  const openDecisions = tokens.filter((t) => t.type === 'decision' && stageIndex(t.stage) < stageIndex('commit'));
  if (openDecisions.length > 0) {
    observations.push(`${openDecisions.length} decision${openDecisions.length === 1 ? '' : 's'} not yet committed.`);
  }
  const owned = tokens.filter((t) => t.owner && t.owner.trim()).length;
  observations.push(`${owned} of ${tokens.length} pieces have a named owner.`);

  // --- Tensions (anti-patterns) ---

  // Deciding while executing: a decision sits at or past Execute.
  for (const t of tokens) {
    if (t.type === 'decision' && stageIndex(t.stage) >= stageIndex('execute')) {
      tensions.push({
        id: `decide-in-${t.id}`,
        severity: 'warn',
        message: `Decision "${labelOf(t)}" sits in the ${STAGE_NAME[t.stage]} zone — work may be moving before the choice is made.`,
        playId: 'escalate',
        tokenIds: [t.id],
      });
    }
  }

  // Ambiguity close to action.
  for (const t of tokens) {
    if (t.type === 'unknown' && stageIndex(t.stage) >= stageIndex('decide')) {
      tensions.push({
        id: `ambiguity-${t.id}`,
        severity: 'warn',
        message: `Unresolved unknown "${labelOf(t)}" sits in ${STAGE_NAME[t.stage]}, close to action.`,
        playId: 'clarify-before-commit',
        tokenIds: [t.id],
      });
    }
  }

  // Work without an owner.
  for (const t of tokens) {
    if (t.type === 'work' && !(t.owner && t.owner.trim())) {
      tensions.push({
        id: `unowned-${t.id}`,
        severity: 'warn',
        message: `Work item "${labelOf(t)}" has no owner.`,
        playId: 'delegate',
        tokenIds: [t.id],
      });
    }
  }

  // Low-confidence irreversible decision.
  for (const t of tokens) {
    if (t.type === 'decision' && t.confidence === 'low' && t.reversibility === 'irreversible') {
      tensions.push({
        id: `risky-decision-${t.id}`,
        severity: 'risk',
        message: `Decision "${labelOf(t)}" is low-confidence and irreversible — the cost of being wrong is high.`,
        playId: 'escalate',
        tokenIds: [t.id],
      });
    }
  }

  // Commitment on shaky ground.
  for (const t of tokens) {
    if (t.type === 'commitment' && t.confidence === 'low') {
      tensions.push({
        id: `shaky-commit-${t.id}`,
        severity: 'warn',
        message: `Commitment "${labelOf(t)}" rests on low confidence.`,
        playId: 'clarify-before-commit',
        tokenIds: [t.id],
      });
    }
  }

  // High-priority risk live in execution.
  for (const t of tokens) {
    if (t.type === 'risk' && t.priority === 'high' && t.stage === 'execute') {
      tensions.push({
        id: `live-risk-${t.id}`,
        severity: 'risk',
        message: `High-priority risk "${labelOf(t)}" is live in execution.`,
        playId: 'surface-constraint',
        tokenIds: [t.id],
      });
    }
  }

  // Execution-heavy with no discovery/framing.
  const executeWork = tokens.filter((t) => t.stage === 'execute' && (t.type === 'work' || t.type === 'decision'));
  const upstream = tokens.filter((t) => t.stage === 'signal' || t.stage === 'frame');
  if (executeWork.length >= 3 && upstream.length === 0) {
    tensions.push({
      id: 'execution-heavy',
      severity: 'info',
      message: 'The field is execution-heavy with nothing in Signal or Frame — little discovery is in play.',
      playId: 'reframe',
      tokenIds: executeWork.map((t) => t.id),
    });
  }

  // Outcomes produced but loop not closed.
  const hasExecute = tokens.some((t) => t.stage === 'execute');
  const hasLearn = tokens.some((t) => t.stage === 'learn');
  if (hasExecute && !hasLearn) {
    tensions.push({
      id: 'open-loop',
      severity: 'info',
      message: 'Work is in Execute but nothing has reached Learn — the feedback loop is open.',
      playId: 'close-loop',
      tokenIds: tokens.filter((t) => t.stage === 'execute').map((t) => t.id),
    });
  }

  return { observations, tensions };
}

export function labelOf(t: PlacedToken): string {
  return t.label && t.label.trim() ? t.label.trim() : tokenDef(t.type).name;
}

/* ------------------------------------------------------------------ */
/* Serialization: the field as structured JSON for an LLM             */
/* ------------------------------------------------------------------ */

export interface SerializedToken {
  id: string;
  type: TokenType;
  typeName: string;
  label: string;
  stage: StageId;
  stageName: string;
  stageIndex: number;
  level: LevelId;
  levelName: string;
  priority?: Priority;
  confidence?: Confidence;
  reversibility?: Reversibility;
  owner?: string;
}

export interface SerializedField {
  stages: { id: StageId; name: string; question: string }[];
  levels: { id: LevelId; name: string }[];
  tokens: SerializedToken[];
  plays: { id: string; name: string; trigger: string }[];
}

const LEVEL_NAME: Record<LevelId, string> = Object.fromEntries(
  LEVELS.map((l) => [l.id, l.name])
) as Record<LevelId, string>;

/**
 * Project a placed field into a clean, self-describing JSON object. This is the
 * exact payload an LLM reads — names and indices are inlined so the model never
 * has to guess what an id means.
 */
export function serializeField(tokens: PlacedToken[]): SerializedField {
  return {
    stages: STAGES.map((s) => ({ id: s.id, name: s.name, question: s.question })),
    levels: LEVELS.map((l) => ({ id: l.id, name: l.name })),
    plays: PLAYS.map((p) => ({ id: p.id, name: p.name, trigger: p.trigger })),
    tokens: tokens.map((t) => ({
      id: t.id,
      type: t.type,
      typeName: tokenDef(t.type).name,
      label: labelOf(t),
      stage: t.stage,
      stageName: STAGE_NAME[t.stage],
      stageIndex: stageIndex(t.stage),
      level: t.level,
      levelName: LEVEL_NAME[t.level],
      priority: t.priority,
      confidence: t.confidence,
      reversibility: t.reversibility,
      owner: t.owner && t.owner.trim() ? t.owner.trim() : undefined,
    })),
  };
}

/* ------------------------------------------------------------------ */
/* Planning a play: where the pieces should move                      */
/* ------------------------------------------------------------------ */

/** Top → bottom on the field is org → individual, so "up" means toward org. */
const LEVEL_UP_ORDER: LevelId[] = ['individual', 'team', 'org'];

function levelUp(level: LevelId): LevelId {
  const i = LEVEL_UP_ORDER.indexOf(level);
  return LEVEL_UP_ORDER[Math.min(i + 1, LEVEL_UP_ORDER.length - 1)];
}

export interface TokenMove {
  tokenId: string;
  from: { stage: StageId; level: LevelId };
  to: { stage: StageId; level: LevelId };
  setOwner?: string;
  reason: string;
}

export interface PlayPlan {
  playId: string;
  playName: string;
  moves: TokenMove[];
  summary: string;
}

/** Compute the target position for one token under a given play, or null if the play does not move it. */
function targetFor(playId: string, t: PlacedToken): { stage: StageId; level: LevelId; setOwner?: string; reason: string } | null {
  switch (playId) {
    case 'escalate': {
      if (t.type !== 'decision') return null;
      const stage = stageIndex(t.stage) > stageIndex('decide') ? 'decide' : t.stage;
      const level = levelUp(t.level);
      if (stage === t.stage && level === t.level) return null;
      return { stage, level, reason: 'Raised to a higher level for explicit approval before action.' };
    }
    case 'clarify-before-commit':
      if (t.stage === 'frame') return null;
      return { stage: 'frame', level: t.level, reason: 'Pulled back to Frame to resolve before agreement.' };
    case 'delegate': {
      if (t.owner && t.owner.trim()) return null;
      return { stage: 'commit', level: t.level, setOwner: 'Owner (TBD)', reason: 'Ownership agreed before execution.' };
    }
    case 'reframe':
      if (t.stage === 'frame' || t.stage === 'signal') return null;
      return { stage: 'frame', level: t.level, reason: 'Work pulled back to restate the problem.' };
    case 'surface-constraint':
      if (t.stage === 'frame') return null;
      return { stage: 'frame', level: t.level, reason: 'Made explicit so it can be planned around.' };
    case 'close-loop':
      if (t.stage === 'learn') return null;
      return { stage: 'learn', level: t.level, reason: 'Outcome carried into Learn to adapt.' };
    default:
      return null;
  }
}

/**
 * Plan a play: given the tokens implicated by the play's tensions, compute the
 * concrete moves to run. Pure and deterministic so the animation, the tests,
 * and any narration all agree on what the play actually does.
 */
export function planPlay(playId: string, tokens: PlacedToken[]): PlayPlan {
  const def = play(playId);
  const playName = def?.name ?? playId;
  const reading = readField(tokens);
  const implicated = new Set<string>();
  for (const tension of reading.tensions) {
    if (tension.playId === playId) tension.tokenIds.forEach((id) => implicated.add(id));
  }

  const moves: TokenMove[] = [];
  for (const t of tokens) {
    if (!implicated.has(t.id)) continue;
    const target = targetFor(playId, t);
    if (!target) continue;
    moves.push({
      tokenId: t.id,
      from: { stage: t.stage, level: t.level },
      to: { stage: target.stage, level: target.level },
      setOwner: target.setOwner,
      reason: target.reason,
    });
  }

  const summary =
    moves.length === 0
      ? `${playName} needs no piece to move on this field — apply it as guidance.`
      : `${playName} moves ${moves.length} piece${moves.length === 1 ? '' : 's'}.`;

  return { playId, playName, moves, summary };
}

/** Apply a plan's moves to a field, returning a new token list. */
export function applyPlan(tokens: PlacedToken[], plan: PlayPlan): PlacedToken[] {
  const byId = new Map(plan.moves.map((m) => [m.tokenId, m]));
  return tokens.map((t) => {
    const m = byId.get(t.id);
    if (!m) return t;
    return {
      ...t,
      stage: m.to.stage,
      level: m.to.level,
      owner: m.setOwner && !(t.owner && t.owner.trim()) ? m.setOwner : t.owner,
    };
  });
}

/**
 * Compose a short natural-language readout of the field — what the AI
 * interpreter would "say back" about the current state.
 */
export function composeFieldSummary(tokens: PlacedToken[]): string {
  const { observations, tensions } = readField(tokens);
  if (tokens.length === 0) return 'The field is empty.';
  const lines: string[] = [];
  lines.push(observations.join(' '));
  if (tensions.length === 0) {
    lines.push('No anti-patterns detected — the field reads as coherent.');
  } else {
    const risks = tensions.filter((t) => t.severity === 'risk').length;
    lines.push(
      `${tensions.length} tension${tensions.length === 1 ? '' : 's'} detected${
        risks ? `, ${risks} high-severity` : ''
      }. Suggested moves: ${[...new Set(tensions.map((t) => t.playId).filter(Boolean))]
        .map((id) => play(id as string)?.name)
        .filter(Boolean)
        .join(', ')}.`
    );
  }
  return lines.join(' ');
}

/* ------------------------------------------------------------------ */
/* Example scenario (a delegation anti-pattern, for first contact)    */
/* ------------------------------------------------------------------ */

export function exampleField(): PlacedToken[] {
  return [
    { id: 'ex-1', type: 'decision', label: 'Vendor choice', stage: 'execute', level: 'team', confidence: 'low', reversibility: 'irreversible' },
    { id: 'ex-2', type: 'work', label: 'Migration build', stage: 'execute', level: 'team' },
    { id: 'ex-3', type: 'unknown', label: 'Budget ceiling', stage: 'commit', level: 'org' },
    { id: 'ex-4', type: 'risk', label: 'Data loss', stage: 'execute', level: 'team', priority: 'high' },
    { id: 'ex-5', type: 'actor', label: 'Platform team', stage: 'execute', level: 'team', owner: 'Platform team' },
  ];
}
